import { useAtomValue, useSetAtom } from "jotai";
import queryString from "query-string";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { checkoutAuthInProgressAtom } from "../atoms/auth/checkoutAuthInProgress";
import { PAGE_HASH } from "../atoms/location/locationAtom";
import { activeUserAtom } from "../atoms/user/activeUserAtom";
import { checkUmgEmailPattern } from "../stories/components/AuthFlow/components/validators/emailValidators";
import { SignInUsernameParameter } from "../stories/components/AuthFlow/constants";
import { SignUpOrInMethodsEnum } from "../stories/components/AuthFlow/types";

// IF YOU CHANGE THIS LIST HERE, YOU MUST CHANGE THIS LIST ON THE BACKEND: no_money_exchanged_label_email
const noMoneyExchangedLabelEmailDomains = [
  "@createmusicgroup.com",
  "@deathrowrecords.com",
  "luke@engineears.com",
];

export function isNoMoneyExchangedLabel(email: string): boolean {
  if (!email || !email.includes("@")) return false;
  return noMoneyExchangedLabelEmailDomains.some((domain) =>
    email.endsWith(domain),
  );
}

// IF YOU CHANGE THIS LIST HERE, YOU MUST CHANGE THIS LIST ON THE BACKEND: is_valid_aandr_email
const validAandREmailDomains: string[] = [
  "300ent.com",
  "alamo-records.com",
  "armadamusic.com",
  "atlanticrecords.com",
  "awal.com",
  "bigmediumsound.com",
  "createmusicgroup.com",
  "capitolrecords.com",
  "columbiarecords.com",
  "concord.com",
  "defected.com",
  "defjam.com",
  "deathrowrecords.com",
  "dreamville.com",
  "emirecords.com",
  "empi.re",
  "engineears.com",
  "epicrecords.com",
  "eqt.co",
  "evglemusic.com",
  "interscope.com",
  "mavinrecords.com",
  "motownrecords.com",
  "neon16.co",
  "ninjatune.net",
  "qualitycontrolmusic.com",
  "rcarecords.com",
  "republicrecords.com",
  "rocnation.com",
  "sonymusic.com",
  "stonesthrow.com",
  "strangemusicinc.com",
  "taylorgang.com",
  "threehundred.biz",
  "txdxe.com",
  "umgconsult.com",
  "umusic.com",
  "unitedmasters.com",
  "virginmusic.com",
  "warnerchappell.com",
  "warnerrecords.com",
  "warprecords.com",
  "wmg.com",
  "vivcortez8@gmail.com",
  "ommiolams@gmail.com",
];

function isValidAandREmail(email: string): boolean {
  if (!email || !email.includes("@")) return false;
  return validAandREmailDomains.some((domain) => email.endsWith(domain));
}

// Why?
export const useDisplayAandRAuth = (
  email: string,
  otpCheckoutEnabled: boolean,
) => {
  const setCheckoutAuth = useSetAtom(checkoutAuthInProgressAtom);
  const navigate = useNavigate();
  const { search } = useLocation();

  const enteredUMGEmail = useMemo(
    () => checkUmgEmailPattern(email, true),
    [email],
  );
  const user = useAtomValue(activeUserAtom);

  useEffect(() => {
    if (user) {
      return;
    }
    if (!otpCheckoutEnabled) {
      setCheckoutAuth(false);
    }
    if (otpCheckoutEnabled) {
      setCheckoutAuth(true);
    }
  }, [otpCheckoutEnabled, setCheckoutAuth, user]);

  const isAandREmail = useMemo(() => {
    return isValidAandREmail(email);
  }, [email]);

  const handleAandRAuth = useCallback(() => {
    if (enteredUMGEmail) return;
    if (!isAandREmail) return;
    navigate({
      hash: PAGE_HASH.SIGN_IN,
      search: queryString.stringify({
        ...queryString.parse(search),
        [SignInUsernameParameter]: email,
        method: SignUpOrInMethodsEnum.INTERNAL,
      }),
    });
  }, [enteredUMGEmail, isAandREmail, navigate, email, search]);

  useEffect(() => {
    if (user) {
      return;
    }
    handleAandRAuth();
  }, [handleAandRAuth, user]);

  return {
    isAandREmail,
    handleAandRAuth,
  };
};
